import { Injectable, Signal, computed, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';

import { APP_ROUTES } from '../../constants/routes';

/**
 * Which section of the app the current route is under.
 *
 * This replaces a pattern where templates read route state from empty
 * `<a routerLinkActive #ref="routerLinkActive">` anchors parked at the bottom
 * of the markup. Those anchors rendered as real, focusable, navigable links
 * with no accessible name: screen readers announced "link" with nothing to
 * identify it, keyboard users hit dead tab stops, and activating one navigated
 * to /error. Because header, footer and the app shell each carried a set, they
 * appeared on every route in the product and were the single highest-volume
 * accessibility defect in the codebase (WCAG 2.4.4, 4.1.2).
 *
 * Matching is prefix-based to mirror routerLinkActive's default, non-exact
 * behavior: /about/privacy counts as being under /about.
 */
@Injectable({ providedIn: 'root' })
export class RouteStateService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  /**
   * Seeded from the BROWSER's URL, not the router's.
   *
   * `router.url` is `/` until the first NavigationEnd, so on a hard load every
   * `isXRoute()` below started false and the shell rendered as though the
   * member were on the home page. On /p/:slug that meant a visitor page
   * painting the signed-in header for a frame: measured at 1440px, "0/3 Goals
   * · Your Goals Report · Login" plus the sidebar and footer, torn down about
   * ten milliseconds later. Spec 07 section 7 names that header as the one
   * thing this page must not carry, and section 1 says why: the visitor is not
   * the member. It also mounted the sidebar long enough to fire the member
   * data reads behind it.
   *
   * `Location.path()` is the deployed path with the base href taken off, which
   * is the same shape `urlAfterRedirects` gives, so the two agree.
   */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.location.path() || '/' }
  );

  public readonly isErrorRoute: Signal<boolean> = computed(() =>
    this.isUnder(APP_ROUTES.Error)
  );
  public readonly isAboutRoute: Signal<boolean> = computed(() =>
    this.isUnder(APP_ROUTES.About.Base)
  );
  public readonly isManagerRoute: Signal<boolean> = computed(() =>
    this.isUnder(APP_ROUTES.Manager.Base)
  );
  public readonly isAnalyticsRoute: Signal<boolean> = computed(() =>
    this.isUnder(APP_ROUTES.Analytics)
  );
  public readonly isAccountRoute: Signal<boolean> = computed(() =>
    this.isUnder(APP_ROUTES.Account.Base)
  );

  /**
   * The public portfolio page, /p/:slug. Spec 07 section 1: it carries no
   * member navigation, because the visitor is not the member. The shell is
   * skipped for it the same way it is for manager, analytics and account.
   */
  public readonly isPublicPageRoute: Signal<boolean> = computed(() =>
    this.isUnder(APP_ROUTES.PublicPage)
  );

  /**
   * The shared goals report, /shared-report/:token. Spec 05 section 8.
   *
   * Same reason as the public portfolio page above: the visitor is not the
   * member. This one is a counselor the member sent a link to, who has no
   * account here, so the page must carry no goal counter, no account control
   * and nothing that offers to sign them in. The shell is skipped for it the
   * same way it is for /p/:slug.
   */
  public readonly isSharedReportRoute: Signal<boolean> = computed(() =>
    this.isUnder(APP_ROUTES.SharedReport)
  );

  /**
   * Pages that carry their own tab row, and so must not also carry the global
   * four-destination bar.
   *
   * NARROWED to the two scrollspy pages. The rule used to cover transition,
   * plan and help as well, reasoning that a page with its own tab strip should
   * not also carry the global bar. In use that reads as the global navigation
   * disappearing: a member on Transition and Jobs has no way back to Quick
   * Match or Resources without the browser's back button, and the four
   * destinations are the shell, not page furniture.
   *
   * Occupation and pathway keep the suppression: their scrollspy sits
   * immediately under the masthead in the same place the global bar would, and
   * stacking the two gives the page two tab rows.
   *
   * The pages that keep their own strip now sit it BELOW the global bar, which
   * is the ordering the shell implies: destination first, then the section's
   * own tabs.
   */
  public readonly hasOwnTabRow: Signal<boolean> = computed(
    () => this.isUnder(APP_ROUTES.Occupation) || this.isUnder(APP_ROUTES.Pathway)
  );

  private isUnder(path: string): boolean {
    const url = this.currentUrl().split(/[?#]/)[0];
    return url === path || url.startsWith(`${path}/`);
  }
}
